import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import API from "Utils/api";
import { getMissionVersions } from "Slices/missionPlans";

interface Version {
  id: string;
  versionNumber: number;
  updatedAt: string;
  createdBy?: string | null;
}

interface VersionSelectorProps {
  missionId: string;
  masterMissionId?: string;
  onVersionSelect: (versionId: string, versionNumber: number) => void;
  selectedVersion?: number;
}

const VersionButton = styled.button`
  height: 48px;
  min-width: 64px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: 1px solid #d1d1d1;
  outline-offset: -1px;
  background: none;
  border: none;
  cursor: pointer;
  color: #1b1b1b;
  font-size: 16px;
  font-family: Readex Pro;
  font-weight: 400;
  gap: 4px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const VersionDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: #ffffff;
  border: 1px solid #d1d1d1;
  border-radius: 0;
  box-shadow: 0px 4px 12px rgba(27, 27, 27, 0.1);
  z-index: 1000;
  min-width: 240px;
`;

const VersionItem = styled.div<{ isSelected: boolean }>`
  padding: 10px 16px;
  cursor: pointer;
  background: ${(props) => (props.isSelected ? "#f4f4f4" : "#ffffff")};

  &:hover {
    background: #f4f4f4;
  }

  &:not(:last-child) {
    border-bottom: 1px solid #f4f4f4;
  }
`;

const VersionItemLabel = styled.div`
  font-size: 14px;
  font-family: Readex Pro;
  font-weight: 400;
  color: #1b1b1b;
`;

const VersionItemMeta = styled.div`
  font-size: 11px;
  font-family: Readex Pro;
  font-weight: 300;
  color: #606060;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 208px;
`;

const VersionContainer = styled.div`
  position: relative;
`;

const VersionArrow = styled.span`
  font-size: 10px;
  margin-left: 2px;
`;

const LoadingText = styled.div`
  padding: 12px 16px;
  color: #606060;
  font-size: 14px;
  font-family: Readex Pro;
  font-weight: 300;
`;

export const VersionSelector: React.FC<VersionSelectorProps> = ({
  missionId,
  masterMissionId,
  onVersionSelect,
  selectedVersion,
}) => {
  // Check for cached versions first
  // Note: MissionVersion TS type uses snake_case but runtime data is camelCase
  // (DRF CamelCase renderer). We cast to Version to access the correct fields.
  const cachedVersions = useSelector(
    masterMissionId ? getMissionVersions(masterMissionId) : () => null
  ) as Version[] | null;

  const [versions, setVersions] = useState<Version[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-select the latest version on mount using cache for speed,
  // or fall back to a one-time API fetch if there's no cache yet.
  useEffect(() => {
    if (selectedVersion !== undefined) return;

    if (cachedVersions && cachedVersions.length > 0) {
      const latest = cachedVersions[0];
      onVersionSelect(latest.id, latest.versionNumber);
    } else {
      // No cache yet — fetch once to get the latest for auto-selection
      API.get(`/missions/${missionId}/versions/`)
        .then((response) => {
          const versionData = response.data;
          if (versionData.length > 0) {
            const latest = versionData[0];
            onVersionSelect(latest.id, latest.versionNumber);
          }
        })
        .catch((err: any) => {
          console.error("Failed to fetch versions:", err);
        });
    }
  }, [missionId]); // intentionally only re-runs when mission changes; uses cache + onVersionSelect at call-time

  // Fetch fresh versions from API every time the dropdown opens so it always
  // shows the current list (including newly created versions + creator names).
  useEffect(() => {
    if (!isOpen) return;

    setLoading(true);
    API.get(`/missions/${missionId}/versions/`)
      .then((response) => {
        setVersions(response.data);
      })
      .catch((err: any) => {
        console.error("Failed to fetch versions:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isOpen, missionId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleVersionClick = (version: Version) => {
    onVersionSelect(version.id, version.versionNumber);
    setIsOpen(false);
  };

  const currentVersionNumber = selectedVersion || 1;

  return (
    <VersionContainer ref={containerRef}>
      <VersionButton
        onClick={handleToggle}
        disabled={loading}
        title="Select version to deploy"
      >
        v{currentVersionNumber}
        <VersionArrow>▼</VersionArrow>
      </VersionButton>
      {isOpen && (
        <VersionDropdown>
          {loading && <LoadingText>Loading...</LoadingText>}
          {!loading && versions.length === 0 && (
            <VersionItem isSelected={true} onClick={() => undefined}>
              v1 (current)
            </VersionItem>
          )}
          {!loading &&
            versions.map((version) => {
              const {
                versionNumber: versionNum,
                updatedAt,
                createdBy,
              } = version;
              const formattedDate = updatedAt
                ? new Date(updatedAt).toLocaleString("en-US", {
                    month: "2-digit",
                    day: "2-digit",
                    year: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : null;
              return (
                <VersionItem
                  key={version.id}
                  isSelected={version.versionNumber === currentVersionNumber}
                  onClick={() => handleVersionClick(version)}
                >
                  <VersionItemLabel>v{versionNum}</VersionItemLabel>
                  {(formattedDate || createdBy) && (
                    <VersionItemMeta>
                      {[formattedDate, createdBy].filter(Boolean).join(" · ")}
                    </VersionItemMeta>
                  )}
                </VersionItem>
              );
            })}
        </VersionDropdown>
      )}
    </VersionContainer>
  );
};
